/**
 * Timeline Component for IRP Workbook
 *
 * Displays a bar chart showing report distribution over time.
 * Supports year and month views.
 * All date operations are performed in UTC without exception.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Card, CardContent, Chip, Typography} from '@mui/material';
import {Bar} from 'react-chartjs-2';
import {
    ActiveElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    ChartDataset,
    ChartEvent,
    ChartOptions,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    TooltipModel
} from 'chart.js';
import {IrpReportLocationDTO} from '../../../../../../@types/integrated-risk-picture';
import {useFullscreenControls} from '../../../../../common/FullscreenWrapper';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
);

type TimeGrouping = 'year' | 'month';
type FitType = 'linear' | 'poly2' | 'poly3';

interface FitResult {
    points: (number | null)[];
    r2?: number;
    slope?: number;
}

interface FitConfig {
    id: FitType;
    label: string;
    color: string;
    compute: (values: number[]) => FitResult;
    summarize: (result: FitResult, grouping: TimeGrouping) => string;
}

interface TimelineProps {
    /** Report location data from the BFF overview/report-locations endpoint. */
    reports?: IrpReportLocationDTO[];
    /** Called when a bar is clicked; provides the UTC date range of the clicked bar. */
    onBarClick?: (dateFrom: Date, dateTo: Date) => void;
    /** Called when "Reset to All Years" is clicked; parent should clear secondaryDateFilter. */
    onReset?: () => void;
    /**
     * When set, forces the timeline into month drill-down view for this UTC year and shows
     * the "Reset to All Years" button. Used to restore state when navigating between tabs
     * while a secondary date filter is already active. Pass null/undefined for year view.
     */
    activeDrillDownYear?: number | null;
    /** 'sparkline' renders a compact preview (bars + hover tooltip) that opens fullscreen on click. */
    variant?: 'full' | 'sparkline';
}

/** Format a UTC year-month key as a short label, e.g. "Jan '23". All in UTC. */
const formatUtcMonthLabel = (utcYear: number, utcMonth: number): string => {
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${MONTHS[utcMonth]} '${String(utcYear).slice(-2)}`;
};

/** Build a "yyyy-MM" key from a Date using UTC fields. */
const utcYearMonthKey = (date: Date): string =>
    `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

/**
 * External (DOM) tooltip for the sparkline. The built-in Chart.js tooltip is clipped
 * to the tiny canvas; this renders a positioned <div> on document.body so it can
 * overflow and stay readable.
 */
const SPARKLINE_TOOLTIP_ID = 'irp-sparkline-tooltip';
const renderSparklineTooltip = (context: { chart: ChartJS; tooltip: TooltipModel<'bar'> }): void => {
    const { chart, tooltip } = context;
    let el = document.getElementById(SPARKLINE_TOOLTIP_ID);
    if (!el) {
        el = document.createElement('div');
        el.id = SPARKLINE_TOOLTIP_ID;
        Object.assign(el.style, {
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: '2000',
            background: 'rgba(33, 33, 33, 0.92)',
            color: '#fff',
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            transform: 'translateX(-50%)',
            transition: 'opacity 0.08s ease',
        });
        document.body.appendChild(el);
    }
    if (tooltip.opacity === 0) {
        el.style.opacity = '0';
        return;
    }
    const title = tooltip.title?.[0] ?? '';
    const value = tooltip.dataPoints?.[0]?.parsed?.y ?? 0;
    el.textContent = `${title} · ${value} reports`;
    // Position just below the (small) chart so it never clips against the top of the page.
    const rect = chart.canvas.getBoundingClientRect();
    el.style.opacity = '1';
    el.style.left = `${rect.left + tooltip.caretX}px`;
    el.style.top = `${rect.bottom + 4}px`;
};

export const computeLinearFit = (values: number[]): FitResult => {
    const n = values.length;
    if (n < 2) return { points: Array(n).fill(values[0] ?? 0), slope: 0, r2: 0 };
    const sumX = (n * (n - 1)) / 2;
    const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
    const sumY = values.reduce((acc, v) => acc + v, 0);
    const sumXY = values.reduce((acc, v, i) => acc + i * v, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const meanY = sumY / n;
    const ssTot = values.reduce((acc, v) => acc + (v - meanY) ** 2, 0);
    const ssRes = values.reduce((acc, v, i) => acc + (v - (slope * i + intercept)) ** 2, 0);
    const r2 = ssTot === 0 ? 1 : 1 - ssRes / ssTot;
    return { points: values.map((_, i) => slope * i + intercept), slope, r2 };
};

const gaussianElimination = (matrix: number[][]): number[] => {
    const n = matrix.length;
    for (let col = 0; col < n; col++) {
        let maxRow = col;
        for (let row = col + 1; row < n; row++) {
            if (Math.abs(matrix[row][col]) > Math.abs(matrix[maxRow][col])) maxRow = row;
        }
        [matrix[col], matrix[maxRow]] = [matrix[maxRow], matrix[col]];
        if (Math.abs(matrix[col][col]) < 1e-10) continue;
        for (let row = col + 1; row < n; row++) {
            const factor = matrix[row][col] / matrix[col][col];
            for (let k = col; k <= n; k++) matrix[row][k] -= factor * matrix[col][k];
        }
    }
    const result = new Array(n).fill(0);
    for (let row = n - 1; row >= 0; row--) {
        result[row] = matrix[row][n];
        for (let col = row + 1; col < n; col++) result[row] -= matrix[row][col] * result[col];
        result[row] /= matrix[row][row];
    }
    return result;
};

export const computePolynomialFit = (values: number[], degree: number): FitResult => {
    const n = values.length;
    if (n <= degree) return { points: new Array(n).fill(null), r2: 0 };
    const d = degree + 1;
    const matrix: number[][] = Array.from({ length: d }, () => new Array(d + 1).fill(0));
    for (let row = 0; row < d; row++) {
        for (let col = 0; col < d; col++) {
            for (let i = 0; i < n; i++) matrix[row][col] += Math.pow(i, row + col);
        }
        for (let i = 0; i < n; i++) matrix[row][d] += Math.pow(i, row) * values[i];
    }
    const coeffs = gaussianElimination(matrix);
    const points = values.map((_, i) => coeffs.reduce((sum, c, p) => sum + c * Math.pow(i, p), 0));
    const meanY = values.reduce((s, v) => s + v, 0) / n;
    const ssTot = values.reduce((s, v) => s + (v - meanY) ** 2, 0);
    const ssRes = values.reduce((s, v, i) => s + (v - points[i]) ** 2, 0);
    const r2 = ssTot === 0 ? 1 : 1 - ssRes / ssTot;
    return { points, r2 };
};

const FIT_CONFIGS: FitConfig[] = [
    {
        id: 'linear',
        label: 'Linear',
        color: 'rgba(211, 47, 47, 0.8)',
        compute: computeLinearFit,
        summarize: (result, grouping): string => {
            const { slope = 0, r2 = 0 } = result;
            const arrow = slope > 0.01 ? '↑' : slope < -0.01 ? '↓' : '→';
            const unit = grouping === 'year' ? 'yr' : 'mo';
            const slopeStr = `${slope >= 0 ? '+' : ''}${slope.toFixed(1)}/${unit}`;
            const r2Label = r2 < 0.3 ? 'weak fit' : r2 < 0.7 ? 'moderate fit' : 'strong fit';
            return `${arrow} ${slopeStr} · R²=${r2.toFixed(3)} (${r2Label})`;
        },
    },
    {
        id: 'poly2',
        label: 'Poly 2',
        color: 'rgba(245, 124, 0, 0.8)',
        compute: (values) => computePolynomialFit(values, 2),
        summarize: (result): string => {
            const { r2 = 0 } = result;
            const r2Label = r2 < 0.3 ? 'weak fit' : r2 < 0.7 ? 'moderate fit' : 'strong fit';
            return `R²=${r2.toFixed(3)} (${r2Label})`;
        },
    },
    {
        id: 'poly3',
        label: 'Poly 3',
        color: 'rgba(156, 39, 176, 0.8)',
        compute: (values) => computePolynomialFit(values, 3),
        summarize: (result): string => {
            const { r2 = 0 } = result;
            const r2Label = r2 < 0.3 ? 'weak fit' : r2 < 0.7 ? 'moderate fit' : 'strong fit';
            return `R²=${r2.toFixed(3)} (${r2Label})`;
        },
    },
];

const Timeline: React.FC<TimelineProps> = ({ reports, onBarClick, onReset, activeDrillDownYear, variant = 'full' }) => {
    // Trend-line fits are only offered when the chart is expanded to fullscreen;
    // the small embedded chart shows only the bars.
    const { isFullscreen, setFullscreen } = useFullscreenControls();
    const [timeGrouping, setTimeGrouping] = useState<TimeGrouping>('year');
    const [drillDownYear, setDrillDownYear] = useState<number | null>(null);
    const [activeFits, setActiveFits] = useState<Set<FitType>>(new Set());

    const toggleFit = useCallback((id: FitType): void => {
        setActiveFits(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    // Sync internal drill-down state when activeDrillDownYear changes externally.
    // This handles tab navigation while a secondary date filter is active: the Timeline
    // remounts in year view by default, but should reflect the existing filter.
    useEffect(() => {
        if (activeDrillDownYear != null) {
            setTimeGrouping('month');
            setDrillDownYear(activeDrillDownYear);
        } else {
            setTimeGrouping('year');
            setDrillDownYear(null);
        }
    }, [activeDrillDownYear]);

    const allReports: IrpReportLocationDTO[] = Array.isArray(reports) ? reports : [];

    // When in month view, restrict chart data to the drilled-down UTC year
    const filteredReports: IrpReportLocationDTO[] = (timeGrouping === 'month' && drillDownYear !== null)
        ? allReports.filter(r => r.dateTime && new Date(r.dateTime as string).getUTCFullYear() === drillDownYear)
        : allReports;

    // Process report data into UTC time buckets
    const chartData = useMemo(() => {
        const timeBuckets = new Map<string, number>();
        const labelToDateRange = new Map<string, { from: Date; to: Date }>();

        // First pass: find UTC date range
        let minDate: Date | null = null;
        let maxDate: Date | null = null;

        filteredReports.forEach(report => {
            if (report.dateTime) {
                const date = new Date(report.dateTime as string);
                if (!isNaN(date.getTime())) {
                    if (!minDate || date < minDate) minDate = date;
                    if (!maxDate || date > maxDate) maxDate = date;
                }
            }
        });

        // Second pass: count reports by UTC time period
        filteredReports.forEach(report => {
            if (report.dateTime) {
                const date = new Date(report.dateTime as string);
                if (!isNaN(date.getTime())) {
                    const key = timeGrouping === 'year'
                        ? date.getUTCFullYear().toString()
                        : utcYearMonthKey(date);
                    timeBuckets.set(key, (timeBuckets.get(key) || 0) + 1);
                }
            }
        });

        // Generate all UTC keys in the range (including zeros)
        let allKeys: string[] = [];

        if (minDate && maxDate) {
            const validMinDate = minDate as Date;
            const validMaxDate = maxDate as Date;

            if (timeGrouping === 'year') {
                const minYear = validMinDate.getUTCFullYear();
                const maxYear = validMaxDate.getUTCFullYear();
                for (let year = minYear; year <= maxYear; year++) {
                    allKeys.push(year.toString());
                }
            } else {
                // Month view: iterate months in UTC
                const current = new Date(Date.UTC(validMinDate.getUTCFullYear(), validMinDate.getUTCMonth(), 1));
                const end = new Date(Date.UTC(validMaxDate.getUTCFullYear(), validMaxDate.getUTCMonth(), 1));
                while (current <= end) {
                    allKeys.push(utcYearMonthKey(current));
                    current.setUTCMonth(current.getUTCMonth() + 1);
                }
                if (allKeys.length > 24) {
                    allKeys = allKeys.slice(-24);
                }
            }
        }

        const displayKeys = allKeys.length > 0 ? allKeys : Array.from(timeBuckets.keys()).sort();

        const labels = displayKeys.map(key => {
            if (timeGrouping === 'year') {
                return key;
            } else {
                const [yr, mo] = key.split('-');
                const utcYear = parseInt(yr, 10);
                const utcMonth = parseInt(mo, 10) - 1;
                const label = formatUtcMonthLabel(utcYear, utcMonth);
                // from = UTC start of month, to = UTC start of month (end calculated on click)
                labelToDateRange.set(label, {
                    from: new Date(Date.UTC(utcYear, utcMonth, 1)),
                    to: new Date(Date.UTC(utcYear, utcMonth, 1)),
                });
                return label;
            }
        });

        const data = displayKeys.map(key => timeBuckets.get(key) || 0);
        const fitResults = new Map<FitType, FitResult>();

        const datasets: ChartDataset<'bar'>[] = [{
            label: 'Reports',
            data,
            backgroundColor: 'rgba(25, 118, 210, 0.8)',
            borderColor: 'rgba(25, 118, 210, 1)',
            borderWidth: 1,
            borderRadius: 4,
        }];

        if (data.length >= 2 && isFullscreen) {
            FIT_CONFIGS
                .filter(c => activeFits.has(c.id))
                .forEach(c => {
                    const result = c.compute(data);
                    fitResults.set(c.id, result);
                    datasets.push({
                        type: 'line' as const,
                        label: c.label,
                        data: result.points,
                        borderColor: c.color,
                        backgroundColor: 'transparent',
                        borderWidth: 2,
                        pointRadius: 0,
                        tension: 0,
                        fill: false,
                    } as unknown as ChartDataset<'bar'>);
                });
        }

        return { chartData: { labels, datasets }, labelToDateRange, fitResults };
    }, [filteredReports, timeGrouping, activeFits, isFullscreen]);

    const { chartData: data, labelToDateRange, fitResults } = chartData;

    // Bar-click handler — all date math in UTC
    const handleBarClick = useCallback((_event: ChartEvent, elements: ActiveElement[], chart: ChartJS): void => {
        if (!onBarClick || elements.length === 0) return;
        const dataIndex = elements[0].index;
        const label = chart.data.labels?.[dataIndex] as string;

        if (timeGrouping === 'year') {
            const year = parseInt(label, 10);
            if (!isNaN(year)) {
                setTimeGrouping('month');
                setDrillDownYear(year);
                // Full UTC year: Jan 1 00:00:00Z to Dec 31 23:59:59.999Z
                onBarClick(
                    new Date(Date.UTC(year, 0, 1)),
                    new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999))
                );
            }
        } else {
            const range = labelToDateRange.get(label);
            if (range) {
                // End of UTC month: day 0 of next month = last day of this month
                const endOfMonth = new Date(Date.UTC(
                    range.from.getUTCFullYear(),
                    range.from.getUTCMonth() + 1,
                    0, 23, 59, 59, 999
                ));
                onBarClick(range.from, endOfMonth);
            }
        }
    }, [timeGrouping, onBarClick, labelToDateRange]);

    const options = useMemo((): ChartOptions<'bar'> => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false
        },
        onClick: handleBarClick,
        onHover: (event, activeElements): void => {
            const canvas = event.native?.target as HTMLCanvasElement;
            if (canvas) {
                canvas.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
            }
        },
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: false
            },
            tooltip: {
                filter: (item): boolean => FIT_CONFIGS.every(c => item.dataset.label !== c.label),
                callbacks: {
                    label: (context): string => `Reports: ${context.parsed.y}`
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    precision: 0
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.05)'
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: timeGrouping === 'month' ? 12 : undefined
                }
            }
        }
    }), [timeGrouping, handleBarClick]);

    const reportsWithDates = filteredReports.filter(r => r.dateTime).length;
    const totalReports = filteredReports.length;

    // Sparkline preview: a compact bars-only chart with a hover tooltip (period + count).
    // Clicking anywhere opens the full, labelled chart in fullscreen (where the fit chips live).
    if (variant === 'sparkline' && !isFullscreen) {
        const sparklineOptions: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { intersect: false, mode: 'index' },
            plugins: {
                legend: { display: false },
                title: { display: false },
                tooltip: {
                    enabled: false,
                    external: renderSparklineTooltip,
                },
            },
            scales: {
                x: { display: false },
                y: { display: false, beginAtZero: true },
            },
        };
        return (
            <Box
                onClick={(): void => setFullscreen(true)}
                sx={{ width: '100%', height: '100%', minHeight: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', px: 0.5 }}
            >
                {reportsWithDates > 0 ? (
                    <Bar data={data} options={sparklineOptions} />
                ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%', textAlign: 'center' }}>
                        No dated reports
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Report Timeline
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {isFullscreen && FIT_CONFIGS.map(config => (
                            <Chip
                                key={config.id}
                                label={config.label}
                                size="small"
                                clickable
                                color={activeFits.has(config.id) ? 'primary' : 'default'}
                                variant={activeFits.has(config.id) ? 'filled' : 'outlined'}
                                onClick={(): void => toggleFit(config.id)}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                        ))}
                        {timeGrouping === 'month' && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={(): void => { setTimeGrouping('year'); setDrillDownYear(null); onReset?.(); }}
                                sx={{ textTransform: 'none' }}
                            >
                                Reset to All Years
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Chart */}
                <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                    {reportsWithDates > 0 ? (
                        <Bar data={data} options={options} />
                    ) : (
                        <Box sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'action.hover',
                            borderRadius: 1
                        }}>
                            <Typography variant="body2" color="text.secondary" align="center">
                                {totalReports === 0
                                    ? 'No reports loaded'
                                    : 'No reports with valid dates'}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Footer stats */}
                {reportsWithDates > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                        Showing {reportsWithDates} of {totalReports} reports
                        {reportsWithDates < totalReports && ` (${totalReports - reportsWithDates} without dates)`}
                        {isFullscreen && activeFits.size > 0 && ` · ${FIT_CONFIGS
                            .filter(c => activeFits.has(c.id))
                            .map(c => {
                                const result = fitResults.get(c.id);
                                if (!result) return c.label;
                                const summary = c.summarize(result, timeGrouping);
                                return summary ? `${c.label}: ${summary}` : c.label;
                            })
                            .join(' · ')}`}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default Timeline;
